import React, { useEffect, useRef, useState, useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../../helper/ThemeToggleButton";
import { PageRefreshProvider, PageRefreshButton } from "../../context/PageRefreshContext";
import { DASHBOARD, LOGIN, SOCIETY_PORTAL, SOCIETY_PORTAL_PROFILE } from "../../Routing/routes";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../redux/actions/AuthActions/LoginAction";
import { menuConfig, filterMenu } from "./menuConfig";
import { selectedSocietyStorer } from "../../utils/webStorer";
import axiosInstance from "../../axios/axiosInstance";
import apiEndpoints from "../../axios/api";
import "./sidebar.css";

// A society is "held" as a member when it reaches this user through a flat they
// own or occupy. Vendor/employee/direct links are staff relationships and get no
// member self-service module.
// Walk menuConfig and produce a flat list of navigable items for the search box
const flattenMenu = (items, parentTitle = "") => {
  const out = [];
  for (const item of items || []) {
    if (item.path) {
      out.push({
        id: item.id,
        title: item.title,
        path: item.path,
        icon: item.icon,
        parentTitle,
      });
    }
    if (Array.isArray(item.submodules)) out.push(...flattenMenu(item.submodules, item.title));
    if (Array.isArray(item.children))   out.push(...flattenMenu(item.children,   parentTitle ? `${parentTitle} › ${item.title}` : item.title));
  }
  return out;
};

// Valid source types for society dropdown (if source_type exists)
const VALID_SOURCE_TYPES = ['DIRECT', 'OWNERSHIP', 'OCCUPANCY', 'VENDOR', 'EMPLOYEE'];

const DEFAULT_LOGO = '/assets/images/society/sunshine-residency-logo.png';

// Disk-stored logos come back as relative /uploads/... paths — resolve them
// against the API origin, not the frontend's.
const resolveAssetUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '').replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const MasterLayout = () => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginData } = useSelector(state => state.loginReducer);

  // Menu search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // Prune the sidebar to what this user may actually open. The permission list
  // is the same one PermissionOnComponent guards routes with, so the menu and
  // the router agree: nothing is offered that would answer with a 403.
  const userPermissions = useSelector((s) => s.UserSocietyReducer?.permissions || []);
  const visibleMenu = useMemo(
    () => filterMenu(menuConfig, userPermissions),
    [userPermissions]
  );

  const flatMenuItems = useMemo(() => flattenMenu(visibleMenu), [visibleMenu]);
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return flatMenuItems
      .filter((it) =>
        it.title.toLowerCase().includes(q)
        || (it.parentTitle || "").toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [searchQuery, flatMenuItems]);

  // Close the search dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchSelect = (path) => {
    setSearchOpen(false);
    setSearchQuery("");
    if (path) navigate(path);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) handleSearchSelect(searchResults[0].path);
  };

  // Filter societies by valid source types
  const userSocieties = useMemo(() => {
    return loginData?.societies?.filter(
      society => VALID_SOURCE_TYPES.includes(society.source_type)
    ) || [];
  }, [loginData?.societies]);

  // Get currently selected society (from localStorage or first one by default)
  const [selectedSociety, setSelectedSociety] = useState(() => selectedSocietyStorer.getSociety());

  // Resolve the active society against the authoritative list and sync it.
  useEffect(() => {
    if (userSocieties.length === 0) return;

    // Match the persisted selection to the canonical object from this list.
    // The stored selection may be a leaner shape (e.g. saved at login from the
    // my-societies endpoint) that lacks society_name — matching here re-adopts
    // the full object so the switcher label always renders. Match by
    // user_society_id first, then society_id; default to the first society.
    const match = userSocieties.find(s =>
      (selectedSociety?.user_society_id != null && s.user_society_id === selectedSociety.user_society_id) ||
      (selectedSociety?.society_id != null && s.society_id === selectedSociety.society_id)
    );
    const resolved = match || userSocieties[0];

    // Update only when the selection is missing, no longer available, or a
    // stale shape — comparing society_name guards against a render loop.
    const needsUpdate =
      !selectedSociety ||
      resolved.user_society_id !== selectedSociety.user_society_id ||
      resolved.society_name !== selectedSociety.society_name;

    if (needsUpdate) {
      setSelectedSociety(resolved);
      selectedSocietyStorer.saveSociety(resolved);
    }
  }, [userSocieties, selectedSociety]);

  // Society branding: sidebar logo (society_logo, falling back to the legacy
  // details.logo_url, then the bundled default) and the footer credit
  // (made_by, falling back to "AccountWale"). Fetched once per active
  // society — neither field is part of the login membership payload.
  const [branding, setBranding] = useState({ madeBy: "", logoUrl: null });
  const activeSocietyId = selectedSociety?.society_id ?? selectedSocietyStorer.getSocietyId();
  useEffect(() => {
    if (!activeSocietyId) return;
    let cancelled = false;
    axiosInstance
      .get(apiEndpoints.GET_SOCIETY_PROFILE_API.replace(":id", activeSocietyId))
      .then((res) => {
        if (cancelled || !res?.success) return;
        setBranding({
          madeBy: (res.data?.made_by || "").trim(),
          logoUrl: resolveAssetUrl(res.data?.society_logo || res.data?.details?.logo_url),
        });
      })
      .catch(() => { /* header/footer fall back to the defaults */ });
    return () => { cancelled = true; };
  }, [activeSocietyId]);
  const societyLogo = branding.logoUrl || DEFAULT_LOGO;

  // Handler to change society and persist to localStorage
  const handleSocietyChange = (society) => {
    // Only switch if it's a different society
    if (selectedSociety?.user_society_id !== society.user_society_id) {
      setSelectedSociety(society);
      selectedSocietyStorer.saveSociety(society);
      // Redirect to dashboard and refresh page to reload all data for new society context
      window.location.href = SOCIETY_PORTAL + DASHBOARD;
    }
  };

  const handleLogout = () => {
    dispatch(logOut({ reason: 'manual', redirect: () => navigate(SOCIETY_PORTAL + LOGIN) }))
  }

  // Find active menu item based on current path
  const findActiveMenuItem = (path) => {
    for (const item of visibleMenu) {
      if (item.type === "single" && item.path === path) {
        return { type: "single", item };
      }
      if (item.type === "dropdown" && item.submodules) {
        for (const submodule of item.submodules) {
          if (submodule.type === "nested" && submodule.children) {
            for (const child of submodule.children) {
              if (path.startsWith(child.path)) {
                return { type: "nested", parent: item, submodule, child };
              }
            }
          } else if (
            path.startsWith(submodule.path) ||
            (submodule.activePaths && submodule.activePaths.some(ap => path.startsWith(ap)))
          ) {
            return { type: "submodule", parent: item, submodule };
          }
        }
      }
    }
    return null;
  };

  // Which Level-1 dropdown and Level-2 nested dropdown are expanded. Previously
  // this was done by querying the DOM, attaching listeners to the anchors and
  // writing inline max-height from scrollHeight. That broke in several ways:
  // scrollHeight reads 0 whenever an ancestor is display:none, the listeners
  // were bound to whatever nodes existed when the effect last ran, and the
  // collapsed-sidebar rules in extra.css pin max-height with !important, which
  // beats an inline style. Driving it from state lets CSS own the animation.
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openNestedId, setOpenNestedId] = useState(null);

  const toggleMenu = (id) => {
    setOpenMenuId((current) => (current === id ? null : id));
    setOpenNestedId(null);
  };

  const toggleNested = (id) => {
    setOpenNestedId((current) => (current === id ? null : id));
  };

  // Expand whichever branch owns the current route.
  useEffect(() => {
    const activeItem = findActiveMenuItem(location.pathname);
    if (activeItem && (activeItem.type === "submodule" || activeItem.type === "nested")) {
      setOpenMenuId(activeItem.parent.id);
      setOpenNestedId(activeItem.type === "nested" ? activeItem.submodule.id : null);
    }
    // A route with no dropdown parent (a Level-1 "single" item) leaves the
    // open branch alone rather than collapsing it, so the menu doesn't jump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  // Render single menu item (Level 1 - no dropdown)
  const renderSingleMenuItem = (item) => (
    <li key={item.id}>
      <NavLink
        to={item.path}
        end
        className={(navData) => (navData.isActive ? "active-page" : "")}
      >
        <Icon icon={item.icon} className='menu-icon' />
        <span>{item.title}</span>
      </NavLink>
    </li>
  );

  // Render submodule item (Level 2 - simple link)
  const renderSubmoduleLink = (submodule) => {
    const isExtraActive = submodule.activePaths
      && submodule.activePaths.some(ap => location.pathname.startsWith(ap));
    return (
    <li key={submodule.id}>
      <NavLink
        to={submodule.path}
        className={(navData) =>
          (navData.isActive || isExtraActive) ? "active-page" : ""
        }
      >
        {submodule.icon && <Icon icon={submodule.icon} className='menu-icon' />}
        {submodule.title}
      </NavLink>
    </li>
  );
  };

  // Render nested submodule (Level 2 with Level 3 children)
  const renderNestedSubmodule = (submodule) => (
    <li
      key={submodule.id}
      className={`dropdown nested-dropdown${openNestedId === submodule.id ? ' open' : ''}`}
      data-submenu-id={submodule.id}
    >
      <Link
        to='#'
        onClick={(e) => { e.preventDefault(); toggleNested(submodule.id); }}
      >
        <span>{submodule.title}</span>
        <Icon icon='ic:round-plus' className='nested-dropdown-indicator' />
      </Link>
      <ul className='sidebar-submenu nested-submenu'>
        {submodule.children.map((child) => (
          <li key={child.id}>
            <NavLink
              to={child.path}
              className={(navData) =>
                navData.isActive ? "active-page" : ""
              }
            >
              {child.icon && <Icon icon={child.icon} className='menu-icon' />}
              {child.title}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );

  // Render dropdown menu item with submodules (Level 1 dropdown)
  const renderDropdownMenuItem = (item) => (
    <li
      key={item.id}
      className={`dropdown${openMenuId === item.id ? ' open' : ''}`}
      data-menu-id={item.id}
    >
      <Link
        to='#'
        onClick={(e) => { e.preventDefault(); toggleMenu(item.id); }}
      >
        <Icon icon={item.icon} className='menu-icon' />
        <span>{item.title}</span>
        <Icon icon='ic:round-plus' className='dropdown-indicator' />
      </Link>
      <ul className='sidebar-submenu'>
        {item.submodules.map((submodule) =>
          submodule.type === "nested"
            ? renderNestedSubmodule(submodule)
            : renderSubmoduleLink(submodule)
        )}
      </ul>
    </li>
  );

  // Render menu items based on type
  const renderMenuItem = (item) => {
    if (item.type === "single") {
      return renderSingleMenuItem(item);
    }
    return renderDropdownMenuItem(item);
  };

  return (
    // Lets each screen register its own fetch with the header Refresh button.
    <PageRefreshProvider>
    <section className={`overlay ${mobileMenu ? " active" : " "}`}>
      {/* sidebar */}
      <aside
        className={`sidebar ${sidebarActive
          ? " active "
          : mobileMenu
            ? " sidebar-open"
            : ""}`
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to={SOCIETY_PORTAL + DASHBOARD} className='sidebar-logo'>
            <img
              src={societyLogo}
              alt='society logo'
              className='light-logo'
              onError={(e) => { if (e.target.src !== window.location.origin + DEFAULT_LOGO) e.target.src = DEFAULT_LOGO; }}
            />
            <img
              src={societyLogo}
              alt='society logo'
              className='dark-logo'
              onError={(e) => { if (e.target.src !== window.location.origin + DEFAULT_LOGO) e.target.src = DEFAULT_LOGO; }}
            />
            <img
              src={societyLogo}
              alt='society logo'
              className='logo-icon'
              onError={(e) => { if (e.target.src !== window.location.origin + DEFAULT_LOGO) e.target.src = DEFAULT_LOGO; }}
            />
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {visibleMenu.map(renderMenuItem)}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header tw-z-20'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                <div className='navbar-search position-relative' style={{ minWidth: 240 }}>
                  <input
                    type='text'
                    name='search'
                    placeholder='Search pages...'
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults[0]) {
                        navigate(searchResults[0].path);
                        setSearchQuery('');
                        setSearchOpen(false);
                      } else if (e.key === 'Escape') {
                        setSearchOpen(false);
                      }
                    }}
                    autoComplete='off'
                  />
                  <Icon icon='ion:search-outline' className='icon' />
                  {searchOpen && searchQuery.trim() && (
                    <div
                      className='position-absolute bg-white shadow rounded'
                      style={{ top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 360, overflowY: 'auto', zIndex: 1050, border: '1px solid #e5e7eb' }}
                    >
                      {searchResults.length === 0 ? (
                        <div className='px-12 py-10 text-sm text-secondary-light'>No matches</div>
                      ) : (
                        searchResults.map((r) => (
                          <button
                            key={r.path}
                            type='button'
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              navigate(r.path);
                              setSearchQuery('');
                              setSearchOpen(false);
                            }}
                            className='d-flex align-items-center gap-2 w-100 text-start border-0 bg-transparent px-12 py-8 hover-bg-neutral-100'
                            style={{ borderBottom: '1px solid #f3f4f6' }}
                          >
                            {r.icon && <Icon icon={r.icon} className='text-primary-600 flex-shrink-0' />}
                            <div className='flex-grow-1' style={{ minWidth: 0 }}>
                              <div className='text-sm fw-medium text-primary-light text-truncate'>{r.title}</div>
                              {r.parent && (
                                <div className='text-xs text-secondary-light text-truncate'>{r.parent}</div>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* Society Switcher Dropdown */}
                {userSocieties.length > 0 && (
                  <div className='dropdown'>
                    <button
                      className='d-flex align-items-center gap-2 px-12 py-8 bg-neutral-100 rounded-pill border-0'
                      type='button'
                      data-bs-toggle='dropdown'
                      style={{ maxWidth: '220px' }}
                    >
                      <Icon
                        icon='mdi:office-building'
                        className='text-primary-600 text-xl flex-shrink-0'
                      />
                      <span className='text-sm fw-medium text-truncate'>
                        {selectedSociety?.society_name || 'Select Society'}
                        {/* Role switch: name the active role when this society
                            has more than one for the user. */}
                        {selectedSociety && userSocieties.filter(s => s.society_id === selectedSociety.society_id).length > 1 && (
                          <span className='text-xs text-secondary-light fw-normal'> · {selectedSociety.role?.role_name}</span>
                        )}
                      </span>
                      <Icon
                        icon='mdi:chevron-down'
                        className='text-secondary-light text-lg flex-shrink-0'
                      />
                    </button>
                    <div className='dropdown-menu to-top dropdown-menu-lg p-0' style={{ minWidth: '280px' }}>
                      <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-8'>
                        <h6 className='text-sm text-primary-light fw-semibold mb-1'>
                          My Societies &amp; Roles
                        </h6>
                        <span className='text-xs text-secondary-light'>
                          {userSocieties.length} account{userSocieties.length !== 1 ? 's' : ''} linked
                          {userSocieties.length > new Set(userSocieties.map(s => s.society_id)).size && ' · pick a role to switch'}
                        </span>
                      </div>
                      <div className='max-h-300-px overflow-y-auto scroll-sm px-8 pb-8'>
                        {userSocieties.map((society) => {
                          // Get flat info from source_data if available
                          const flatInfo = society.source_data?.flat;
                          const wingTitle = flatInfo?.wing?.title || flatInfo?.wing?.number;
                          const flatNumber = flatInfo?.flat_number;

                          return (
                            <button
                              key={society.user_society_id}
                              type='button'
                              className={`w-100 text-start d-flex align-items-center gap-3 p-12 radius-8 border-0 mb-4 ${selectedSociety?.user_society_id === society.user_society_id
                                ? 'bg-primary-50'
                                : 'bg-transparent hover-bg-neutral-100'
                                }`}
                              onClick={() => handleSocietyChange(society)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className='w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0'>
                                <Icon
                                  icon='mdi:office-building'
                                  className='text-primary-600 text-lg'
                                />
                              </div>
                              <div className='flex-grow-1 min-w-0'>
                                <h6 className='text-sm fw-semibold mb-1 text-truncate'>
                                  {society.society_name}
                                </h6>
                                <div className='d-flex align-items-center gap-2 flex-wrap'>
                                  <span className='badge bg-neutral-200 text-secondary-light text-xs px-8 py-2 radius-4'>
                                    {society.role?.role_name || society.source_type}
                                  </span>
                                  {flatNumber && (
                                    <span className='text-xs text-secondary-light'>
                                      {wingTitle ? `${wingTitle}-` : ''}{flatNumber}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {selectedSociety?.user_society_id === society.user_society_id && (
                                <Icon
                                  icon='mdi:check-circle'
                                  className='text-primary-600 text-xl flex-shrink-0'
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Re-runs only the current screen's own API calls; hidden when
                    the screen has registered nothing to refresh. */}
                <PageRefreshButton />

                <ThemeToggleButton />

                <div className='dropdown'>
                  <button
                    className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <Icon
                      icon='iconoir:bell'
                      className='text-primary-light text-xl'
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                    <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                          Notifications
                        </h6>
                      </div>
                      <span className='text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center'>
                        0
                      </span>
                    </div>
                    <div className='max-h-400-px overflow-y-auto scroll-sm pe-4'>
                      <div className='px-24 py-12 text-center text-secondary-light'>
                        No new notifications
                      </div>
                    </div>
                  </div>
                </div>

                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle p-0 border-0'
                    type='button'
                    data-bs-toggle='dropdown'
                    style={{ width: '40px', height: '40px' }}
                  >
                    {loginData?.user?.first_name || loginData?.user?.last_name ? (
                      <div className="tw-w-full tw-h-full tw-rounded-full tw-bg-amber-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-shadow-sm tw-text-sm">
                        {`${loginData?.user?.first_name?.charAt(0) || ''}${loginData?.user?.last_name?.charAt(0) || ''}`.toUpperCase()}
                      </div>
                    ) : (
                      <div className="tw-w-full tw-h-full tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                        <Icon icon='solar:user-circle-bold-duotone' className='tw-text-2xl tw-text-amber-500' />
                      </div>
                    )}
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm tw-rounded-xl tw-border tw-border-slate-200 tw-shadow-lg tw-p-0 tw-overflow-hidden' style={{ minWidth: '240px' }}>
                    {/* User Info Header */}
                    <div className='tw-bg-amber-50 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-3'>
                      <div className='tw-w-10 tw-h-10 tw-rounded-full tw-bg-amber-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-sm tw-flex-shrink-0'>
                        {`${loginData?.user?.first_name?.charAt(0) || ''}${loginData?.user?.last_name?.charAt(0) || ''}`.toUpperCase()}
                      </div>
                      <div className='tw-min-w-0 tw-flex-1'>
                        <p className='tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate tw-mb-0 tw-leading-tight'>
                          {loginData?.user?.first_name + ' ' || 'User'}
                          {loginData?.user?.last_name || ''}
                        </p>
                        <p className='tw-text-xs tw-text-slate-500 tw-mb-0 tw-mt-0.5'>
                          {loginData?.user?.role || 'User'}
                        </p>
                      </div>
                    </div>
                    {/* Menu Items */}
                    <div className='tw-py-1.5 tw-px-1.5'>
                      <Link
                        className='tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-text-slate-700 hover:tw-bg-amber-50 hover:tw-text-amber-700 tw-transition-colors tw-no-underline'
                        to={SOCIETY_PORTAL + SOCIETY_PORTAL_PROFILE}
                      >
                        <div className='tw-w-7 tw-h-7 tw-rounded-md tw-bg-amber-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0'>
                          <Icon icon='solar:user-rounded-bold-duotone' className='tw-text-base tw-text-amber-600' />
                        </div>
                        My Profile
                      </Link>
                      <div className='tw-my-1 tw-border-t tw-border-slate-100 tw-mx-3'></div>
                      <Link
                        className='tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors tw-no-underline'
                        onClick={handleLogout}
                      >
                        <div className='tw-w-7 tw-h-7 tw-rounded-md tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0'>
                          <Icon icon='solar:logout-2-bold-duotone' className='tw-text-base tw-text-red-500' />
                        </div>
                        Log Out
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-main-body'><Outlet /></div>

        <footer className='d-footer'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <p className='mb-0'>© {new Date().getFullYear()} {"Society Portal"}. All Rights Reserved.</p>
            </div>
            <div className='col-auto'>
              <p className='mb-0'>
                Made by <span className='text-primary-600'>{branding.madeBy || 'AccountWale'}</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
    </PageRefreshProvider>
  );
};

export default MasterLayout;
